import React, { useState } from 'react';
import { useMutation } from 'react-query';
import { additionalActService } from '../../../services/additionalActService';
import { Download, Mail, AlertCircle, FileText, CheckCircle, RefreshCw } from 'lucide-react';
import { Clock, Eye, Send, PenTool, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import SearchableSelect from '../../../components/common/SearchableSelect';

const ACT_TYPES = {
  PRELUNGIRE: 'Prelungire',
  MODIFICARE_SERVICII: 'ModificareServicii',
};

const ACT_TYPE_LABELS = {
  [ACT_TYPES.PRELUNGIRE]: 'Prelungire contract',
  [ACT_TYPES.MODIFICARE_SERVICII]: 'Modificare servicii și preț',
};

const STATUS_DISPLAY_MAP_TEXT = {
  'Ciornă': { label: 'Ciornă', color: 'bg-gray-100 text-gray-600', Icon: FileText },
  'Generat': { label: 'Generat', color: 'bg-blue-100 text-blue-600', Icon: FileText },
  'Trimis': { label: 'Trimis', color: 'bg-indigo-100 text-indigo-600', Icon: Send },
  'Accesat': { label: 'Accesat', color: 'bg-purple-100 text-purple-600', Icon: Eye },
  'Semnat': { label: 'Semnat', color: 'bg-green-100 text-green-800', Icon: CheckCircle },
  'Expirat': { label: 'Expirat', color: 'bg-red-100 text-red-800', Icon: Clock },
  'Reziliat': { label: 'Reziliat', color: 'bg-red-100 text-red-800', Icon: AlertCircle },
  'Necunoscut': { label: 'Necunoscut', color: 'bg-yellow-100 text-yellow-600', Icon: AlertCircle },
};

const getActStatusDisplay = (act) => {
  const statusText = act.statusText || 'Necunoscut';
  return STATUS_DISPLAY_MAP_TEXT[statusText] || STATUS_DISPLAY_MAP_TEXT['Necunoscut'];
};

// Serviciile curente ale contractului, ca punct de plecare pentru noua Anexa 1
const mapContractServices = (contractServices) =>
  contractServices.map((s) => ({
    serviceId: s.serviceId,
    quantity: s.quantity,
    customPrice: s.price,
  }));

const AdditionalActActions = ({
  contractId,
  contractNumber,
  additionalActs,
  formatDate,
  onActChange,
  client,
  availableServices = [],
  contractServices = [],
}) => {
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [actNumber, setActNumber] = useState('1');
  const [newEndDate, setNewEndDate] = useState('');
  const [actType, setActType] = useState(ACT_TYPES.PRELUNGIRE);
  const [actServices, setActServices] = useState([]);
  const [accessSessionsCount, setAccessSessionsCount] = useState(1);
  const [emailAddresses, setEmailAddresses] = useState('');
  const [includeClientEmails, setIncludeClientEmails] = useState(true);
  const [createdAct, setCreatedAct] = useState(null);
  const [actToActOn, setActToActOn] = useState(null);
  const [actionType, setActionType] = useState('create-new');
  const [message, setMessage] = useState(null);

  const generatePdfMutation = useMutation(
    (actId) => additionalActService.generatePdf(actId),
    {
      onSuccess: () => {
        if (onActChange) onActChange();
        toast.success('PDF generat cu succes');
      },
      onError: (error) => {
        console.error('PDF generation error:', error);
        toast.error('Eroare la generarea PDF: ' + (error.response?.data?.message || error.message));
      }
    }
  );

  const handleGeneratePdf = (actId) => {
    generatePdfMutation.mutate(actId);
  };

  const openModal = (type, act = null) => {
    setActionType(type);
    setActToActOn(act);
    setActNumber(act ? act.number : '1');
    setNewEndDate('');
    setActType(ACT_TYPES.PRELUNGIRE);
    setActServices(mapContractServices(contractServices));
    setAccessSessionsCount(1);
    setEmailAddresses('');
    setIncludeClientEmails(true); // Bifat by default
    setShowModal(true);
    setMessage(null);
  };

  const isServicesAct = actType === ACT_TYPES.MODIFICARE_SERVICII;

  const addActService = () => {
    setActServices((prev) => [...prev, { serviceId: '', quantity: 1, customPrice: null }]);
  };

  const removeActService = (index) => {
    setActServices((prev) => prev.filter((_, i) => i !== index));
  };

  const updateActService = (index, field, value) => {
    setActServices((prev) => prev.map((service, i) => {
      if (i !== index) return service;
      if (field === 'serviceId') {
        // La schimbarea serviciului se preia prețul din catalog
        const selected = availableServices.find((s) => s.id.toString() === value?.toString());
        return { ...service, serviceId: value, customPrice: selected ? selected.price : null };
      }
      return { ...service, [field]: value };
    }));
  };

  const getActServicesTotal = () =>
    actServices.reduce((total, s) => total + (Number(s.quantity) || 0) * (Number(s.customPrice) || 0), 0);

  const initiateFileDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Funcție helper pentru a obține email-urile finale
  const getFinalEmails = () => {
    // Parse manual emails
    const manualEmails = emailAddresses
      .split(/[,\s]+/)
      .map(email => email.trim())
      .filter(email => email.length > 0);

    // Collect client emails if checkbox is checked
    const clientEmails = [];
    if (includeClientEmails && client) {
      if (client.email) clientEmails.push(client.email);
      if (client.representativeEmail) clientEmails.push(client.representativeEmail);
    }

    // Combine and deduplicate
    const allEmails = [...new Set([...manualEmails, ...clientEmails])];
    return allEmails;
  };

  const handleCreateNewAct = async (shouldSend) => {
    if (!actNumber || !newEndDate) {
      setMessage({ type: 'error', text: 'Numărul actului și data sunt obligatorii' });
      return;
    }

    if (isServicesAct) {
      if (actServices.length === 0 || actServices.some((s) => !s.serviceId || !(s.quantity > 0))) {
        setMessage({ type: 'error', text: 'Selectați cel puțin un serviciu, fiecare cu cantitate mai mare decât 0' });
        return;
      }
      if (!(accessSessionsCount > 0)) {
        setMessage({ type: 'error', text: 'Numărul de sesiuni de acces trebuie să fie cel puțin 1' });
        return;
      }
    }

    // Get final emails ONLY if sending
    const emails = shouldSend ? getFinalEmails() : [];

    // Validate emails if sending
    if (shouldSend && emails.length === 0) {
      setMessage({ 
        type: 'error', 
        text: 'Trebuie să specificați cel puțin o adresă de email pentru trimitere' 
      });
      return;
    }

    const createRequest = {
      ContractId: contractId,
      Date: newEndDate,
      Number: actNumber,
      ActType: actType,
      ...(isServicesAct && {
        AccessSessionsCount: parseInt(accessSessionsCount),
        Services: actServices.map((s) => ({
          ServiceId: parseInt(s.serviceId),
          Quantity: parseInt(s.quantity),
          CustomPrice: s.customPrice !== null && s.customPrice !== '' ? parseFloat(s.customPrice) : null,
        })),
      }),
      AutoGeneratePdf: true,
      SendToClient: shouldSend,
      NotificationEmails: emails,
    };

    try {
      setLoading(true);
      setMessage(null);
      setCreatedAct(null);

      const newAct = await additionalActService.create(createRequest);

      setCreatedAct(newAct);
      setShowModal(false);

      if (onActChange) {
        onActChange();
      }

      let successText = `Actul adițional nr. ${newAct.number} a fost creat cu succes!`;
      if (shouldSend) {
        successText += ` Email-ul a fost trimis către ${emails.length} destinatar${emails.length !== 1 ? 'i' : ''}.`;
      } else {
        successText += ' Acum îl puteți descărca.';
      }

      setMessage({ type: 'success', text: successText });

    } catch (error) {
      console.error('Eroare la crearea actului adițional:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.Message || error.response?.data?.message || 'Eroare la crearea actului adițional'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExistingPdf = async (actId) => {
    try {
      setLoading(true);
      setMessage(null);

      const { blob, filename } = await additionalActService.downloadPdf(actId);

      initiateFileDownload(blob, filename);

      setMessage({ type: 'success', text: `Actul adițional a fost descărcat cu succes!` });

    } catch (error) {
      console.error('Eroare la descărcarea actului adițional:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.Message || 'Eroare la descărcarea PDF-ului actului adițional'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendExistingAct = async () => {
    if (!actToActOn?.id) return;

    const emails = getFinalEmails();
    const requestBody = emails;

    console.log('Sending existing act with payload:', requestBody);
    try {
      setLoading(true);
      setMessage(null);

      const result = await additionalActService.sendAdditionalAct(
        actToActOn.id,
        requestBody
      );

      const recipientsCount = result.recipients || 0;
      setMessage({
        type: 'success',
        text: `${result.message} (${recipientsCount} destinatar${recipientsCount !== 1 ? 'i' : ''})`
      });

      setShowModal(false);
      setEmailAddresses('');
      setActToActOn(null);

      if (onActChange) onActChange();

    } catch (error) {
      console.error('Eroare la trimiterea actului adițional:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.Message || 'Eroare la trimiterea actului adițional'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (actionType === 'create-new') {
      handleCreateNewAct(false);
    } else if (actionType === 'send-new') {
      handleCreateNewAct(true);
    } else if (actionType === 'send-existing') {
      handleSendExistingAct();
    }
  };

  const getModalTitle = () => {
    switch (actionType) {
      case 'create-new':
        return 'Generează Act Adițional';
      case 'send-new':
        return 'Generează și Trimite Act Adițional';
      case 'send-existing':
        return `Trimite Act Adițional Existent ${actToActOn?.number}`;
      default:
        return 'Act Adițional';
    }
  };

  const showActDetails = actionType === 'create-new' || actionType === 'send-new';
  const showEmailField = actionType === 'send-new' || actionType === 'send-existing';

  // Helper pentru afișarea email-urilor clientului
  const getClientEmailsDisplay = () => {
    if (!client) return null;
    const emails = [];
    if (client.email) emails.push(client.email);
    if (client.representativeEmail) emails.push(client.representativeEmail);
    return emails.length > 0 ? emails.join(', ') : null;
  };

  const clientEmailsDisplay = getClientEmailsDisplay();

  return (
    <div className="additional-act-actions">
      {message && (
        <div className={`alert alert-${message.type}`}>
          <AlertCircle size={16} />
          <span>{message.text}</span>
          <button onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      <div className="action-buttons mb-4">
        <button
          onClick={() => openModal('create-new')}
          disabled={loading}
          className="btn btn-primary"
        >
          <FileText size={16} />
          Generează Act Adițional Nou
        </button>

        <button
          onClick={() => openModal('send-new')}
          disabled={loading}
          className="btn btn-secondary"
        >
          <Mail size={16} />
          Generează și Trimite Act Nou
        </button>
      </div>

      {additionalActs && additionalActs.length > 0 && (
        <div className="act-list-container">
          <h2 className="list-heading">
            Acte Adiționale Existente
          </h2>
          <div className="space-y-3">
            {additionalActs.map((act) => {
              const statusDisplay = getActStatusDisplay(act);
              const IconComponent = statusDisplay.Icon;
              return (
                <div key={act.id} className="act-list-item">
                  <div>
                    <div className="act-number">
                      Act Adițional nr. {act.number}
                      <span className="act-type"> · {ACT_TYPE_LABELS[act.actType] || ACT_TYPE_LABELS[ACT_TYPES.PRELUNGIRE]}</span>
                    </div>
                    <div className="act-date">
                      {formatDate(act.date)}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`status-badge ${statusDisplay.color}`}>
                      <IconComponent className="w-3 h-3 mr-1" />
                      {statusDisplay.label}
                    </span>
                    {!act.pdfFilePath ? (
                      <button
                        onClick={() => handleGeneratePdf(act.id)}
                        disabled={generatePdfMutation.isLoading}
                        className="btn-link-primary"
                        title="Generează PDF"
                      >
                        <FileText size={16} />
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => handleGeneratePdf(act.id)}
                          disabled={generatePdfMutation.isLoading}
                          className="btn-link"
                          title="Regenerează PDF"
                        >
                          <RefreshCw size={16} />
                        </button>
                        <button
                          onClick={() => handleDownloadExistingPdf(act.id)}
                          disabled={loading}
                          className="btn-link"
                          title="Descarcă PDF-ul Actului"
                        >
                          <Download size={16} />
                        </button>
                      </>
                    )}

                    <button
                      onClick={() => openModal('send-existing', act)}
                      disabled={loading}
                      className="btn-link text-secondary-500"
                      title="Trimite Actul Adițional"
                    >
                      <Mail size={16} />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className={`modal-content ${isServicesAct && showActDetails ? 'modal-wide' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {getModalTitle()}
              </h3>
              <button onClick={() => setShowModal(false)}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">

                {showActDetails && (
                  <>
                    <div className="form-group">
                      <label htmlFor="actType">
                        Tip Act Adițional *
                      </label>
                      <select
                        id="actType"
                        value={actType}
                        onChange={(e) => setActType(e.target.value)}
                        className="form-control"
                      >
                        {Object.values(ACT_TYPES).map((type) => (
                          <option key={type} value={type}>{ACT_TYPE_LABELS[type]}</option>
                        ))}
                      </select>
                      {isServicesAct && (
                        <span className="hint">
                          Modifică art. 4.1 (prețul), art. 5.0 (sesiunile de acces) și înlocuiește Anexa 1 cu serviciile de mai jos
                        </span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="actNumber">
                        Număr Act Adițional *
                      </label>
                      <input
                        id="actNumber"
                        type="text"
                        value={actNumber}
                        onChange={(e) => setActNumber(e.target.value)}
                        placeholder="1"
                        required
                        className="form-control"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="newEndDate">
                        {isServicesAct ? 'Data Intrării în Vigoare *' : 'Noua Dată de Sfârșit *'}
                      </label>
                      <input
                        id="newEndDate"
                        type="date"
                        value={newEndDate}
                        onChange={(e) => setNewEndDate(e.target.value)}
                        min={isServicesAct ? undefined : new Date().toISOString().split('T')[0]}
                        required
                        className="form-control"
                      />
                      <span className="hint">
                        {isServicesAct
                          ? 'Data de la care se aplică noile prețuri și noua Anexa 1'
                          : 'Data până la care se prelungește contractul'}
                      </span>
                    </div>

                    {isServicesAct && (
                      <>
                        <div className="form-group">
                          <label htmlFor="accessSessionsCount">
                            Număr Sesiuni de Acces *
                          </label>
                          <input
                            id="accessSessionsCount"
                            type="number"
                            min="1"
                            value={accessSessionsCount}
                            onChange={(e) => setAccessSessionsCount(parseInt(e.target.value) || '')}
                            required
                            className="form-control"
                          />
                          <span className="hint">Înlocuiește „implicit una - 1” din art. 5.0</span>
                        </div>

                        <div className="form-group">
                          <div className="services-header">
                            <label>Servicii Anexa 1 *</label>
                            <button type="button" onClick={addActService} className="btn-link-primary">
                              <Plus size={16} /> Adaugă serviciu
                            </button>
                          </div>

                          {actServices.length === 0 && (
                            <span className="hint">Nu există servicii. Adăugați cel puțin unul.</span>
                          )}

                          {actServices.map((service, index) => (
                            <div key={index} className="service-row">
                              <div className="service-select">
                                <SearchableSelect
                                  options={availableServices}
                                  value={service.serviceId}
                                  onChange={(value) => updateActService(index, 'serviceId', value)}
                                  placeholder="Selectați serviciul"
                                  searchPlaceholder="Căutare serviciu..."
                                  getOptionLabel={(s) => s.name}
                                  getOptionValue={(s) => s.id}
                                  emptyMessage="Nu s-au găsit servicii"
                                />
                              </div>
                              <input
                                type="number"
                                min="1"
                                value={service.quantity}
                                onChange={(e) => updateActService(index, 'quantity', parseInt(e.target.value) || 1)}
                                className="form-control service-qty"
                                title="Cantitate"
                              />
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={service.customPrice ?? ''}
                                onChange={(e) => updateActService(index, 'customPrice', e.target.value === '' ? null : parseFloat(e.target.value))}
                                className="form-control service-price"
                                placeholder="Preț"
                                title="Preț unitar (lei)"
                              />
                              <button
                                type="button"
                                onClick={() => removeActService(index)}
                                className="btn-link text-danger"
                                title="Șterge serviciul"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          ))}

                          {actServices.length > 0 && (
                            <div className="services-total">
                              Total lunar nou: <strong>{getActServicesTotal().toFixed(2)} lei</strong>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </>
                )}

                {showEmailField && (
                  <>
                    <div className="form-group">
                      <label htmlFor="emailAddresses">
                        Adrese Email Adiționale
                        <span className="hint">
                          Adaugă email-uri suplimentare (separați prin virgulă sau spațiu)
                        </span>
                      </label>
                      <textarea
                        id="emailAddresses"
                        value={emailAddresses}
                        onChange={(e) => setEmailAddresses(e.target.value)}
                        placeholder="exemplu@email.ro, alt@email.ro"
                        rows={3}
                        className="form-control"
                      />
                    </div>

                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={includeClientEmails}
                          onChange={(e) => setIncludeClientEmails(e.target.checked)}
                          className="checkbox-input"
                        />
                        <span className="checkbox-text">
                          Include email-urile clientului
                        </span>
                      </label>
                      {includeClientEmails && clientEmailsDisplay && (
                        <div className="client-emails-display">
                          <Mail className="w-4 h-4" />
                          <span>{clientEmailsDisplay}</span>
                        </div>
                      )}
                      {includeClientEmails && !clientEmailsDisplay && (
                        <div className="client-emails-warning">
                          <AlertCircle className="w-4 h-4" />
                          <span>Clientul nu are email-uri configurate</span>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn btn-secondary"
                >
                  Anulează
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn btn-primary"
                >
                  {loading ? 'Se procesează...' : (actionType.includes('create') ? 'Generează Act' : 'Trimite Act')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        .additional-act-actions {
          margin: 20px 0;
        }

        .alert {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          border-radius: 6px;
          margin-bottom: 16px;
        }

        .alert-success {
          background-color: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }

        .alert-error {
          background-color: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }

        .alert button {
          margin-left: auto;
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
        }

        .action-buttons {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .mb-4 {
          margin-bottom: 1rem;
        }

        .btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          border: 1px solid transparent;
        }

        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-primary {
          background-color: #007bff;
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background-color: #0056b3;
        }

        .btn-secondary {
          background-color: #6c757d;
          color: white;
        }

        .btn-secondary:hover:not(:disabled) {
          background-color: #545b62;
        }
        
        .act-list-container {
            background-color: #ffffff;
            border-radius: 8px;
            box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
            border: 1px solid #e5e7eb;
            padding: 24px;
        }

        .list-heading {
            font-size: 18px;
            font-weight: 600;
            color: #1f2937;
            margin-bottom: 16px;
        }

        .space-y-3 > * + * {
            margin-top: 0.75rem;
        }

        .act-list-item {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 12px;
            background-color: #f9fafb;
            border-radius: 6px;
        }

        .act-number {
            font-size: 14px;
            font-weight: 500;
            color: #1f2937;
        }

        .act-date {
            font-size: 14px;
            color: #6b7280;
        }

        .status-badge {
            display: inline-flex;
            align-items: center;
            padding: 2.5px 10px;
            border-radius: 9999px;
            font-size: 12px;
            font-weight: 500;
        }

        .status-badge .w-3 {
            width: 12px;
            height: 12px;
            margin-right: 4px;
        }
        
        .btn-link {
            background: none;
            border: none;
            cursor: pointer;
            padding: 8px;
            color: #10b981;
            transition: color 0.2s;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .btn-link:hover {
            color: #059669;
        }

        .btn-link-primary {
            background: none;
            border: none;
            cursor: pointer;
            padding: 8px;
            color: #007bff;
            transition: color 0.2s;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .btn-link-primary:hover {
            color: #0056b3;
        }

        .text-secondary-500 {
            color: #6c757d;
        }

        .text-secondary-500:hover {
             color: #545b62;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          border-radius: 8px;
          width: 90%;
          max-width: 500px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .modal-content.modal-wide {
          max-width: 760px;
        }

        .modal-content .modal-body {
          max-height: 70vh;
          overflow-y: auto;
        }

        .act-type {
          font-weight: 400;
          color: #6b7280;
        }

        .services-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .services-header label {
          margin-bottom: 0;
        }

        .service-row {
          display: flex;
          gap: 8px;
          align-items: center;
          margin-bottom: 8px;
        }

        .service-select {
          flex: 1;
          min-width: 0;
        }

        .service-qty {
          width: 80px !important;
        }

        .service-price {
          width: 110px !important;
        }

        .text-danger {
          color: #dc2626;
        }

        .text-danger:hover {
          color: #b91c1c;
        }

        .services-total {
          text-align: right;
          font-size: 14px;
          margin-top: 8px;
        }

        @media (max-width: 600px) {
          .service-row {
            flex-wrap: wrap;
          }

          .service-select {
            flex-basis: 100%;
          }
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px 20px;
          border-bottom: 1px solid #dee2e6;
        }

        .modal-header h3 {
          margin: 0;
          font-size: 18px;
        }

        .modal-header button {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          opacity: 0.7;
        }

        .modal-header button:hover {
          opacity: 1;
        }

        .modal-body {
          padding: 20px;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 20px;
          border-top: 1px solid #dee2e6;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-group label {
          display: block;
          margin-bottom: 8px;
          font-weight: 500;
          font-size: 14px;
        }

        .form-control {
          width: 100%;
          padding: 10px;
          border: 1px solid #ced4da;
          border-radius: 4px;
          font-size: 14px;
        }

        .form-control:focus {
          outline: none;
          border-color: #007bff;
          box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.25);
        }

        .hint {
          display: block;
          font-size: 12px;
          color: #6c757d;
          margin-top: 4px;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          margin-bottom: 8px;
        }

        .checkbox-input {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        .checkbox-text {
          font-size: 14px;
          font-weight: 500;
          color: #1f2937;
        }

        .client-emails-display {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px;
          background-color: #e0f2fe;
          border: 1px solid #bae6fd;
          border-radius: 6px;
          margin-top: 8px;
          font-size: 13px;
          color: #075985;
        }

        .client-emails-display .w-4 {
          width: 16px;
          height: 16px;
          flex-shrink: 0;
        }

        .client-emails-warning {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px;
          background-color: #fef3c7;
          border: 1px solid #fde68a;
          border-radius: 6px;
          margin-top: 8px;
          font-size: 13px;
          color: #92400e;
        }

        .client-emails-warning .w-4 {
          width: 16px;
          height: 16px;
          flex-shrink: 0;
        }
      `}</style>
    </div>
  );
};

export default AdditionalActActions;